import * as classnames from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { classes } from '../../utils/Classes';
import { Heading4, Heading5, heading3Theme } from 'elc-typography';
import { breakpoint, ContentSpacing } from 'elc-base-theme';
import { ProductDisplayNameTheme, ProductSublineNameTheme } from '../../../theme/default-theme';
import { PRODUCT_BRIEF_NAME } from '../../constants/DataTestIdAttributes';

export interface IConfig {
    productId: string;
}

export interface IProductName {
    className?: string;
    productUrl?: string;
    displayName: string;
    subDisplayName?: string;
    showSubDisplayName?: boolean;
    subHeader?: string;
    isRenderedOnProductFull?: boolean;
    onProductNameClick?(): void;
}

const ProductNameWrapper = styled.div`
    margin: ${ContentSpacing.space12} 0;
    ${breakpoint('desktop')`
        margin: ${ContentSpacing.space12} 0 0;
    `};
`;

const ProductSubheader = styled(Heading4)`
    ${ProductSublineNameTheme}
    margin: ${ContentSpacing.space8} 0;
`;

const ProductDisplayName = styled.a`
    ${heading3Theme}
    ${ProductDisplayNameTheme}
    margin: ${ContentSpacing.space16} 0;
    ${breakpoint('desktop')`
        text-align: ${(props: IProductName) => (props.isRenderedOnProductFull ? 'left' : 'center')};
    `};

    &:hover {
        text-decoration: underline;
    }
`;

const SublineName = styled(Heading5)`
    ${ProductSublineNameTheme}
    margin: ${ContentSpacing.space8} 0;
    ${breakpoint('desktop')`
        text-align: ${(props: IProductName) => (props.isRenderedOnProductFull ? 'left' : 'center')};
    `};
`;

export const ProductName = observer((props: IProductName) => {
    const productDisplayNameClassNames = classnames(
        'elc-product-display-name',
        'js-product-display-name'
    );

    const productSubDisplayNameClassNames = classnames(
        'elc-product-sub-display-name',
        'js-product-sub-display-name'
    );

    const productSubheaderClassNames = classes('product-subheader');

    const {
        subDisplayName,
        showSubDisplayName,
        displayName,
        onProductNameClick,
        subHeader
    } = props;

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        if (onProductNameClick) {
            onProductNameClick();
        }
    };

    return (
        <ProductNameWrapper className="elc-product-name-wrapper">
            <ProductDisplayName
                href={props.productUrl}
                title={displayName}
                aria-label={displayName}
                tabIndex={0}
                onClick={handleClick}
                className={productDisplayNameClassNames}
                data-test-id={PRODUCT_BRIEF_NAME}
                dangerouslySetInnerHTML={{ __html: displayName }}
                {...props}
            />
            {subHeader && (
                <ProductSubheader
                    className={productSubheaderClassNames}
                    dangerouslySetInnerHTML={{ __html: subHeader }}
                />
            )}
            {showSubDisplayName && subDisplayName && (
                <SublineName
                    className={productSubDisplayNameClassNames}
                    dangerouslySetInnerHTML={{ __html: subDisplayName }}
                    {...props}
                />
            )}
        </ProductNameWrapper>
    );
});
